import React from 'react';
import styled, { withTheme } from 'styled-components';
import hexToRgba from 'hex-to-rgba';
import { FlexColumn, Flex } from '../../../../Reusable/Container';
import { AmtDecimalSubscript } from '../../../../Reusable/AmountLabel';
import { Tag, Text } from '../../../../Reusable/Text';
import { toCurrency } from '../../../../../Utils/util';

// Styled components for the chart
const ProgressBarContainer = styled(Flex)`
  width: 100%;
  margin: ${props => props.margin || '8px 0'};
`;

const ProgressSegment = styled.div`
  width: ${props => `${props.percent}%`};
  height: ${props => props.height || '8px'};
  background-color: ${props => props.color};
  border-radius: ${props => {
        if (props.isFirst && props.isLast) return `${props.borderRadius}px` || '4px';
        if (props.isFirst) return `${props.borderRadius || 4}px 0px 0px ${props.borderRadius || 4}px`;
        if (props.isLast) return `0px ${props.borderRadius || 4}px ${props.borderRadius || 4}px 0px`;
        return '0';
    }};
`;

const LegendItem = styled(Flex)`
  margin-right: ${props => props.marginRight || '0px'};
`;

const LegendDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => props.color};
  /* margin-right: 8px; */
`;

const PaymentProgressChart = (props) => {
    var {
        totalAmount = 0,
        paidAmount = 0,
        scheduledAmount = 0,
        remainingAmount = 0, // Will be calculated if not provided
        data = null,
        height = 10,
        borderRadius = 5,
        showLabels = true,
        showLegend = true,
        opacity = 1
    } = props;

    var colorPaid = props.theme.colors.primary9,
        colorScheduled = props.theme.colors.primary,
        colorRemaining = props.theme.colors.systemGray6;
    // If data is provided, use it to calculate amounts
    if (data) {
        // Process the bill object if provided
        totalAmount = data.Amount || 0;

        // Calculate paid amount (completed payments)
        paidAmount = data.Payments
            ? data.Payments.filter(p => p.PaymentStatus === 'Completed')
                .reduce((sum, payment) => sum + Number(payment.Amount), 0)
            : 0;

        // Calculate scheduled amount
        scheduledAmount = data.Payments
            ? data.Payments.filter(p => p.PaymentStatus === 'Scheduled')
                .reduce((sum, payment) => sum + Number(payment.Amount), 0)
            : 0;
    }

    // Calculate remaining amount if not provided
    if (remainingAmount === 0) {
        remainingAmount = totalAmount - paidAmount - scheduledAmount;
    }

    // Calculate percentages for width
    const paidPercent = (paidAmount / totalAmount) * 100 || 0;
    const scheduledPercent = (scheduledAmount / totalAmount) * 100 || 0;
    const remainingPercent = (remainingAmount / totalAmount) * 100 || 0;

    // Apply opacity to colors
    const paidColor = opacity !== 1 ? hexToRgba(colorPaid, opacity) : colorPaid;
    const scheduledColor = opacity !== 1 ? hexToRgba(colorScheduled, opacity) : colorScheduled;
    const remainingColor = opacity !== 1 ? hexToRgba(colorRemaining, opacity) : colorRemaining;

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    return (
        <FlexColumn fullWidth>
            {/* Progress bar */}
            <ProgressBarContainer>
                {paidAmount > 0 && (
                    <ProgressSegment
                        percent={paidPercent}
                        height={`${height}px`}
                        color={paidColor}
                        borderRadius={borderRadius}
                        isFirst={true}
                        isLast={scheduledAmount === 0 && remainingAmount === 0}
                        title={`Paid: ${formatCurrency(paidAmount)}`}
                    />
                )}

                {scheduledAmount > 0 && (
                    <ProgressSegment
                        percent={scheduledPercent}
                        height={`${height}px`}
                        color={scheduledColor}
                        borderRadius={borderRadius}
                        isFirst={paidAmount === 0}
                        isLast={remainingAmount === 0}
                        title={`Scheduled: ${formatCurrency(scheduledAmount)}`}
                    />
                )}

                {remainingAmount > 0 && (
                    <ProgressSegment
                        percent={remainingPercent}
                        height={`${height}px`}
                        color={remainingColor}
                        borderRadius={borderRadius}
                        isFirst={paidAmount === 0 && scheduledAmount === 0}
                        isLast={true}
                        title={`Remaining: ${formatCurrency(remainingAmount)}`}
                    />
                )}
            </ProgressBarContainer>

            {/* Legend */}
            {showLegend && (
                <Flex between style={{ marginTop: '8px' }}>
                    <Flex start style={{ marginTop: '0px' }} wrap>
                        {paidAmount > 0 && (
                            <LegendItem centerH gap='8px' marginRight="24px">
                                <LegendDot color={paidColor} />
                                <Text lightText>Paid: </Text>
                                <AmtDecimalSubscript size='16px' subscriptSize='14px' weight={400} subscriptWeight={400} paddingBottom='5px' amt={"$"+toCurrency(paidAmount)} />
                                {/* <Tag size='12px' color={paidColor} radius='12px' padding='2px 10px'>Paid</Tag> */}
                            </LegendItem>
                        )}

                        {scheduledAmount > 0 && (
                            <LegendItem centerH gap='8px' marginRight="24px">
                                <LegendDot color={scheduledColor} />
                                <Text lightText>Scheduled: </Text>
                                <AmtDecimalSubscript size='16px' subscriptSize='14px' weight={400} subscriptWeight={400} paddingBottom='5px' amt={"$"+toCurrency(scheduledAmount)} />
                                {/* <Tag size='12px' color={scheduledColor} radius='12px' padding='2px 10px'>Scheduled</Tag> */}
                            </LegendItem>
                        )}
                    </Flex>
                    <LegendItem centerH gap='8px'>
                        <LegendDot color={remainingColor} />
                        <Text lightText>Balance: </Text>
                        <AmtDecimalSubscript size='16px' subscriptSize='14px' weight={600} subscriptWeight={400} paddingBottom='5px' amt={"$"+toCurrency(remainingAmount)} />
                    </LegendItem>
                </Flex>
                
            )}
        </FlexColumn>
    );
};

export default withTheme(PaymentProgressChart);